'use client';

import { Chevron } from '@ahamove/icons';
import { Nationwide } from '@ahamove/icons/pages';
import {
  Avatar,
  Badge,
  Button,
  Skeleton,
  Stack,
  Text,
  Title,
  rem,
  type ButtonProps,
} from '@mantine/core';
import { InfoIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import BookingServiceInfoTooltip from './services/booking-service-info-tooltip';
import { useBookingService } from './services/use-booking-service';
import CustomComponent from '@/components/modals/modal-custom-drawer';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type Props = ButtonProps;
// eslint-disable-next-line sonarjs/cognitive-complexity
export default function BookingService(props: Props) {
  const isMobile = useIsMobile();
  const localeSuffix = useLocaleSuffix();
  const tBooking = useTranslations('booking');
  const { isLoadingStore, isOpenPopupAllService } = useBookingStore(
    (state) => state
  );

  const {
    onOpenPopupAllService,
    onClosePopupAllService,
    selectedService,
    pickupPath,
    renderEstimatedServices,
    isLoading,
    isDisabled,
  } = useBookingService({
    enabled: isMobile,
  });

  if (!isMobile) {
    return null;
  }

  if (isLoading) {
    return (
      <Skeleton radius={rem(12)} h={{ base: rem(48), md: rem(56) }} w="100%" />
    );
  }

  return (
    <>
      <Button
        loading={isLoadingStore}
        disabled={isDisabled}
        fullWidth
        h="auto"
        mih={{ base: rem(48), md: rem(56) }}
        radius={rem(12)}
        pl={{ base: rem(8), md: rem(12) }}
        py={{ base: rem(8), md: rem(12) }}
        onClick={onOpenPopupAllService}
        pr="md"
        variant="white"
        leftSection={
          <Avatar
            radius="sm"
            variant="outline"
            size={rem(32)}
            p={0}
            flex="none"
            src={selectedService?.icon_url}
            alt={selectedService?.name}
            styles={{
              placeholder: {
                border: 'none',
              },
            }}
          >
            {pickupPath && selectedService ? (
              <Nationwide className="size-8" />
            ) : (
              <InfoIcon width={16} height={16} />
            )}
          </Avatar>
        }
        rightSection={
          selectedService ? <Chevron className="size-4 md:size-6" /> : undefined
        }
        justify="space-between"
        styles={{
          root: {
            overflow: 'visible',
          },
          label: {
            width: '100%',
          },
        }}
        {...props}
      >
        <Stack gap={rem(4)} ta="left" w="100%">
          {pickupPath && selectedService ? (
            <>
              <Title
                order={isMobile ? 6 : 4}
                fw={selectedService && pickupPath ? 700 : 500}
                c="neutral.6"
                className="truncate"
              >
                {selectedService[`name_${localeSuffix}`]}{' '}
                <BookingServiceInfoTooltip service={selectedService} />
              </Title>
              {selectedService?.[`description_${localeSuffix}`] ? (
                <Text size="sm" fw={500} c="neutral.7" truncate>
                  {selectedService[`description_${localeSuffix}`]}
                </Text>
              ) : null}
            </>
          ) : null}

          {!pickupPath ? (
            <Text size="sm" fw={500} c="neutral.7" truncate>
              {tBooking('service.required_pickup_location')}
            </Text>
          ) : null}

          {pickupPath && !selectedService ? (
            <Text size="sm" fw={500} c="neutral.7" truncate>
              {tBooking('service.no_data')}
            </Text>
          ) : null}
        </Stack>
        {selectedService?.badge ? (
          <Badge
            tt="capitalize"
            size="md"
            radius="sm"
            pos="absolute"
            fw={600}
            top={rem(-8)}
            right={rem(-6)}
            bg={selectedService.badge.background_color ?? 'red.6'}
            c={selectedService.badge.text_color ?? 'white'}
          >
            {selectedService.badge[`description_${localeSuffix}`] || 'Mới'}
          </Badge>
        ) : null}
      </Button>
      <CustomComponent
        title={tBooking('service.choose_service')}
        isOpen={isOpenPopupAllService}
        onClose={onClosePopupAllService}
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
            borderBottom: `1px solid var(--mantine-color-neutral-0)`,
            paddingLeft: rem(16),
          },
          bodyStyle: {
            padding: 0,
          },
        }}
      >
        {renderEstimatedServices()}
      </CustomComponent>
    </>
  );
}
